<template>
  <div class="space-y-2" :class="props.centerText ? 'text-center' : ''">
    <LabelComponent v-if="!hideLabel" :for="formatLabel()">
      {{ props.label }}
    </LabelComponent>

    <div class="relative">
      <span
        class="absolute left-4 top-1/2 -translate-y-1/2 text-grey-light-900"
        :class="{ 'text-4xl font-bold': isLargeInput }"
      >
        R
      </span>

      <Input
        type="text"
        class="pl-7 text-grey-light-900"
        :id="formatLabel()"
        :class="{ 'text-center': props.centerText, 'text-4xl font-bold px-10 py-8': isLargeInput }"
        v-model="displayValue"
        :inputmode="allowCents ? 'decimal' : 'numeric'"
        :disabled="disabled"
        :maxlength="props.maxLength"
        @input="onInput"
      />
    </div>
    <template v-if="!hideError && error">
      <p
        v-if="!hideError && error"
        class="text-sm font-medium"
        :class="{
          'text-warning-400': isValid,
          'text-error-500': !isValid,
          'text-center': props.centerText,
        }"
      >
        {{ error }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { Input } from '@/components/ui/input';
import { displayCurrency, toCents } from '@/utils/format.utils';
import { ref, watch } from 'vue';
import LabelComponent from '../ui/label/LabelComponent.vue';

const props = withDefaults(
  defineProps<{
    label: string;
    modelValue: number; // in cents
    disabled?: boolean;
    min?: number; // cents
    max?: number; // cents
    warningMin?: number; // cents
    warningMax?: number; // cents
    hideError?: boolean;
    hideLabel?: boolean;
    allowCents?: boolean;
    isLargeInput?: boolean;
    centerText?: boolean;
    maxLength?: number;
  }>(),
  {
    min: 0,
    max: 99999999,
    warningMin: 0,
    warningMax: 99999999,
    hideError: false,
    hideLabel: false,
    allowCents: true,
    isLargeInput: false,
    centerText: false,
  },
);

const emit = defineEmits<{
  (e: 'update:isValid', value: boolean): void;
  (e: 'update:modelValue', value: number): void;
}>();

const error = ref('');
const isValid = ref(true);
const displayValue = ref('');

const cleanValue = (raw: string): string => {
  let value = raw;

  if (props.allowCents) {
    value = value.replace(/[^0-9.]/g, ''); // only digits & dot
    value = value.replace(/(\..*)\./g, '$1'); // only one dot
    if (value.includes('.')) {
      const [whole, decimals = ''] = value.split('.');
      value = `${whole}.${decimals.substring(0, 2)}`;
    }
    // if (value.endsWith('.')) value = value.slice(0, -1);
  } else {
    // only digits, no decimals
    value = value.replace(/[^0-9]/g, '');
    if (value.includes('.')) value = value.split('.')[0];
  }

  // remove leading zeros (except "0." case)
  if (value && value !== '.' && value !== '0.' && !value.startsWith('0.')) {
    value = value.replace(/^0+(\d)/, '$1');
  }

  return value;
};

const validateCents = (cents: number) => {
  if (props.min > cents) {
    error.value = `Amount must be at least ${displayCurrency(props.min)}`;
    isValid.value = false;
  } else if (props.max < cents) {
    error.value = `Amount must not exceed ${displayCurrency(props.max)}`;
    isValid.value = false;
  } else if (props.warningMax < cents) {
    error.value = `Warning: Amount exceeds the recommended maximum of ${displayCurrency(props.warningMax)}`;
    isValid.value = true;
  } else if (props.warningMin > cents) {
    error.value = `Warning: Amount is below the recommended minimum of ${displayCurrency(props.warningMin)}`;
    isValid.value = true;
  } else {
    error.value = '';
    isValid.value = true;
  }

  emit('update:isValid', isValid.value);
};

watch(
  () => props.modelValue,
  (newVal) => {
    if (typeof newVal === 'number' && !isNaN(newVal)) {
      displayValue.value = (newVal / 100).toString();
      validateCents(newVal);
    }
  },
  { immediate: true },
);

const onInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const cleaned = cleanValue(input.value);
  input.value = cleaned;
  displayValue.value = cleaned;

  const parsed = parseFloat(cleaned || '0');
  const cents = toCents(isNaN(parsed) ? 0 : parsed);

  validateCents(cents);
  emit('update:modelValue', cents);
};

const formatLabel = () => props.label.toLowerCase().replace(/\s+/g, '-');
</script>
